import { http } from '@/boot/plugins'
import type {
  AuthSession,
  AuthUser,
  ForgotPasswordPayload,
  LoginPayload,
  LoginResponse,
  ResetPasswordPayload,
  TwoFactorPayload,
} from '@/types/auth'

/**
 * Transport only — no toast, no router, no store. Callers branch on the `kind`
 * the response interceptor attaches to a failure.
 */
export default {
  /**
   * Verifies the password and returns a single-use challenge token. No access
   * token is issued until the second factor is verified.
   */
  async login(payload: LoginPayload): Promise<LoginResponse> {
    const { data } = await http.post<LoginResponse>('/login', payload)
    return data
  },

  /** Redeems the challenge token. The password is deliberately not sent again. */
  async verifyTwoFactor(payload: TwoFactorPayload): Promise<AuthSession> {
    const { data } = await http.post<AuthSession>('/google2fa', payload)
    return data
  },

  async logout(): Promise<void> {
    await http.post('/logout')
  },

  /** Revokes only the token making the request; `logout` revokes every device. */
  async logoutCurrentDevice(): Promise<void> {
    await http.post('/logout/current')
  },

  async authUser(): Promise<AuthUser> {
    const { data } = await http.get<AuthUser>('/auth-user')
    return data
  },

  /** EMR's idle check reads the token's last use, so an active user keeps it fresh with this. */
  async ping(): Promise<void> {
    await http.post('/ping')
  },

  async verifyEmail(url: string): Promise<{ message: string }> {
    const { data } = await http.get<{ message: string }>(url)
    return data
  },

  async generateSso(): Promise<{ token: string }> {
    const { data } = await http.post<{ token: string }>('/sso/generate')
    return data
  },

  async forgotPassword(payload: ForgotPasswordPayload): Promise<{ message: string }> {
    const { data } = await http.post<{ message: string }>('/reset-password-link', payload)
    return data
  },

  async resetPassword(payload: ResetPasswordPayload): Promise<{ message: string }> {
    const { data } = await http.post<{ message: string }>('/reset-password', payload)
    return data
  },
}
